import CrudSection from "../components/CrudSection";
import { StatusTextBadge } from "../../../components/base/StatusTextBadge";
import type { CrudColumn, CrudField } from "../types";

// ===========================================================================
// Acceso vehicular — bespoke sections.
// ===========================================================================

// El tablero de Acceso vehicular vive en screens/VehicleAccessDashboard.tsx
// (API real + Control de estacionamientos, CC-CONSOLE #1594).

// --- Plumas y barreras -----------------------------------------------------
interface Pluma extends Record<string, unknown> {
  id: string; nombre: string; ubicacion: string; modo: string; estado: string;
}
const plumaColumns: CrudColumn<Pluma>[] = [
  { key: "nombre", label: "Nombre" },
  { key: "ubicacion", label: "Ubicación" },
  { key: "modo", label: "Modo" },
  { key: "estado", label: "Estado", align: "center", render: (r) => <StatusTextBadge value={r.estado} /> },
];
const plumaFields: CrudField[] = [
  { key: "nombre", label: "Nombre", required: true, placeholder: "Pluma sur" },
  { key: "ubicacion", label: "Ubicación", required: true, placeholder: "Estacionamiento visitantes" },
  { key: "modo", label: "Modo", type: "select", defaultValue: "Automático", options: [
    { value: "Automático", label: "Automático (LPR)" }, { value: "Manual", label: "Manual" }, { value: "Mixto", label: "Mixto" },
  ] },
  { key: "estado", label: "Estado", type: "select", defaultValue: "Activo", options: [
    { value: "Activo", label: "Activo" }, { value: "Inactivo", label: "Inactivo" }, { value: "Mantenimiento", label: "Mantenimiento" },
  ] },
];
const plumaSeed: Pluma[] = [
  { id: "pl1", nombre: "Pluma sur", ubicacion: "Estacionamiento principal", modo: "Automático", estado: "Activo" },
  { id: "pl2", nombre: "Pluma norte", ubicacion: "Acceso visitantes", modo: "Mixto", estado: "Activo" },
  { id: "pl3", nombre: "Barrera servicio", ubicacion: "Patio de maniobras", modo: "Manual", estado: "Mantenimiento" },
];
export const PlumasSection = () => (
  <CrudSection<Pluma> title="Plumas y barreras" subtitle="Control de accesos vehiculares"
    icon="ri-parking-box-line" columns={plumaColumns} fields={plumaFields} seed={plumaSeed} searchKey="nombre" />
);

// --- Vehículos -------------------------------------------------------------
interface Vehiculo extends Record<string, unknown> {
  id: string; placa: string; propietario: string; perfil: string; tag: string; estado: string;
}
const vehiculoColumns: CrudColumn<Vehiculo>[] = [
  { key: "placa", label: "Placa" },
  { key: "propietario", label: "Propietario" },
  { key: "perfil", label: "Perfil" },
  { key: "tag", label: "Tag" },
  { key: "estado", label: "Estado", align: "center", render: (r) => <StatusTextBadge value={r.estado} /> },
];
const vehiculoFields: CrudField[] = [
  { key: "placa", label: "Placa", required: true, placeholder: "ABC-123-D" },
  { key: "propietario", label: "Propietario", required: true, placeholder: "Nombre / unidad" },
  { key: "perfil", label: "Perfil", type: "select", defaultValue: "Residente", options: [
    { value: "Residente", label: "Residente" }, { value: "Miembro", label: "Miembro" },
    { value: "Visitante", label: "Visitante" }, { value: "Proveedor", label: "Proveedor" },
  ] },
  { key: "tag", label: "Tag / TAG ID", placeholder: "TAG-0001" },
  { key: "estado", label: "Estado", type: "select", defaultValue: "Activo", options: [
    { value: "Activo", label: "Activo" }, { value: "Bloqueado", label: "Bloqueado" },
  ] },
];
const vehiculoSeed: Vehiculo[] = [
  { id: "v1", placa: "ABC-123-D", propietario: "Depto 402", perfil: "Residente", tag: "TAG-0042", estado: "Activo" },
  { id: "v2", placa: "XYZ-987-A", propietario: "Casa 12", perfil: "Residente", tag: "TAG-0091", estado: "Activo" },
  { id: "v3", placa: "JKL-555-B", propietario: "Limpieza S.A.", perfil: "Proveedor", tag: "—", estado: "Bloqueado" },
];
export const VehiculosSection = () => (
  <CrudSection<Vehiculo> title="Vehículos" subtitle="Vehículos registrados por perfil"
    icon="ri-car-line" columns={vehiculoColumns} fields={vehiculoFields} seed={vehiculoSeed} searchKey="placa" />
);

// --- Lectura de placas (log) -----------------------------------------------
interface Lectura extends Record<string, unknown> {
  id: string; fecha: string; placa: string; pluma: string; confianza: string; resultado: string;
}
const lecturaColumns: CrudColumn<Lectura>[] = [
  { key: "fecha", label: "Fecha y hora" },
  { key: "placa", label: "Placa detectada" },
  { key: "pluma", label: "Pluma" },
  { key: "confianza", label: "Confianza" },
  { key: "resultado", label: "Resultado", align: "center", render: (r) => <StatusTextBadge value={r.resultado} /> },
];
const lecturaSeed: Lectura[] = [
  { id: "l1", fecha: "2026-06-08 10:31", placa: "ABC-123-D", pluma: "Pluma sur", confianza: "98%", resultado: "Concedido" },
  { id: "l2", fecha: "2026-06-08 10:27", placa: "—", pluma: "Pluma norte", confianza: "41%", resultado: "Denegado" },
  { id: "l3", fecha: "2026-06-08 10:19", placa: "XYZ-987-A", pluma: "Pluma sur", confianza: "95%", resultado: "Concedido" },
];
export const LecturaPlacasSection = () => (
  <CrudSection<Lectura> title="Lectura de placas" subtitle="Reconocimiento automático (LPR)"
    icon="ri-camera-lens-line" columns={lecturaColumns} fields={[]} seed={lecturaSeed} searchKey="placa" readOnly />
);

// --- Registro vehicular (log) ----------------------------------------------
interface RegistroVeh extends Record<string, unknown> {
  id: string; fecha: string; placa: string; tipo: string; pluma: string;
}
const registroColumns: CrudColumn<RegistroVeh>[] = [
  { key: "fecha", label: "Fecha y hora" },
  { key: "placa", label: "Placa" },
  { key: "tipo", label: "Movimiento", align: "center", render: (r) => <StatusTextBadge value={r.tipo} /> },
  { key: "pluma", label: "Pluma" },
];
const registroSeed: RegistroVeh[] = [
  { id: "rv1", fecha: "2026-06-08 10:31", placa: "ABC-123-D", tipo: "Entrada", pluma: "Pluma sur" },
  { id: "rv2", fecha: "2026-06-08 10:19", placa: "XYZ-987-A", tipo: "Salida", pluma: "Pluma sur" },
  { id: "rv3", fecha: "2026-06-08 09:58", placa: "MNO-222-C", tipo: "Entrada", pluma: "Pluma norte" },
];
export const RegistroVehicularSection = () => (
  <CrudSection<RegistroVeh> title="Registro vehicular" subtitle="Historial de entradas y salidas de vehículos"
    icon="ri-history-line" columns={registroColumns} fields={[]} seed={registroSeed} searchKey="placa" readOnly />
);
